import * as React from 'react';
import { useEffect, useState } from 'react';

import { BarChart, Box, Button, Link, Modal } from '@amzn/awsui-components-react-v3';
import { i18nStrings } from 'src/commons/tables';
import {
  COMPLETED,
  CREATED,
  EXPIRED,
  PENDING_CONSUMER_REVIEW,
  PENDING_CUSTODIAN_REVIEW,
  TOTAL,
} from 'src/commons/constants';

export interface ReviewStatusChartModalProps {
  reviews: any[];
  visible: boolean;
  dismiss: any;
  reviewSeason: string;
}

const STATUS_CONFIG = [
  { key: CREATED, label: 'Created' },
  { key: PENDING_CUSTODIAN_REVIEW, label: 'Pending custodian review' },
  { key: PENDING_CONSUMER_REVIEW, label: 'Pending consumer review' },
  { key: COMPLETED, label: 'Completed' },
  { key: EXPIRED, label: 'Expired' },
];

const createChartSeries = (reviewsByTag) =>
  Array.from(reviewsByTag, ([tag, statusCounts]) => ({
    title: tag + '(Total:' + statusCounts.get(TOTAL) + ')',
    type: 'bar',
    data: STATUS_CONFIG.map(({ key, label }) => ({ x: label, y: statusCounts.get(key) })),
  }));

export const ReviewStatusChartModal = (props: ReviewStatusChartModalProps) => {
  const [loadingModal, setLoadingModal] = useState(false);
  const [series, setSeries] = useState([]);
  const [yDomain, setYDomain] = useState(undefined);

  const groupReviewsByTag = (reviews) => {
    const reviewsByTag = new Map();
    let maxCount = 0;

    reviews
      .filter((review) => review?.status && review?.tag)
      .forEach((review) => {
        const tag = review.tag.toString();

        if (!reviewsByTag.has(tag)) {
          let map = new Map(STATUS_CONFIG.map(({ key }) => [key, 0]));
          map.set(TOTAL, 0);
          reviewsByTag.set(tag, map);
        }

        const statusCounts = reviewsByTag.get(tag);
        const updatedCount = statusCounts.get(review.status) + 1;
        statusCounts.set(review.status, updatedCount);
        maxCount = Math.max(maxCount, updatedCount);
        statusCounts.set(TOTAL, statusCounts.get(TOTAL) + 1);
      });

    setYDomain([0, maxCount]);
    return reviewsByTag;
  };

  useEffect(() => {
    if (props.reviewSeason && props.reviews) {
      setLoadingModal(true);
      const reviewsByTag = groupReviewsByTag(props.reviews);
      const chartSeries = createChartSeries(reviewsByTag);
      setSeries(chartSeries);
      setLoadingModal(false);
    }
  }, [props.reviewSeason, props.reviews]);

  if (loadingModal) return null;

  return (
    <Modal
      onDismiss={props.dismiss}
      visible={props.visible}
      closeAriaLabel='Close modal'
      size='max'
      header='Selected season review status dashboard'
    >
      <BarChart
        series={series}
        xDomain={STATUS_CONFIG.map(({ label }) => label)}
        yDomain={yDomain}
        detailPopoverSeriesContent={({ series, y }) => ({
          key: series.title,
          value: (
            <Link href='#' ariaLabel={`See details for ${y} on ${series.title}`}>
              {y}
            </Link>
          ),
        })}
        ariaLabel='Review status bar chart'
        height={300}
        xTitle='Review status'
        yTitle='Count'
        i18nStrings={{
          ...i18nStrings,
          filterLabel: 'Filter displayed review tags',
          filterPlaceholder: 'Filter review tag',
        }}
        empty={
          <Box textAlign='center' color='inherit'>
            <b>No data available</b>
            <Box variant='p' color='inherit'>
              There is no data available
            </Box>
          </Box>
        }
        noMatch={
          <Box textAlign='center' color='inherit'>
            <b>No matching data</b>
            <Box variant='p' color='inherit'>
              There is no matching data to display
            </Box>
            <Button>Clear filter</Button>
          </Box>
        }
      />
    </Modal>
  );
};
